import { useState } from 'react';
import { Badge, Chargement, Erreur, Infobulle, Modale } from './Elements';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { formaterDateHeure } from '../lib/format';
import type { BulletinFichePaie, EtatFichesPaie, FicheEnvoyee, LigneFichePaie } from '../types';

/** L'enveloppe de la synthese mensuelle. */
export function IconeEnveloppe() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="1.8" y="3.2" width="12.4" height="9.6" rx="1.4" />
      <path d="M2.2 4.2 8 8.8l5.8-4.6" />
    </svg>
  );
}

type Resultat = { etat: 'en_cours' } | { etat: 'envoye'; adresse: string } | { etat: 'echec'; message: string };

/**
 * ENVOYER LEUR FICHE DE PAIE AUX SALARIES (2026-11-04), depuis la synthese
 * d'un mois cloture. La meme modale pour un salarie ou pour tous : elle
 * montre, ligne par ligne, l'adresse, le bulletin trouve sur le Drive et ce
 * qui a deja ete envoye. Le serveur retrouve lui-meme l'adresse et le
 * fichier : l'ecran ne lui designe qu'un contrat.
 *
 * LE LOT SE JOUE ICI, un appel par salarie, l'un apres l'autre : l'avancement
 * se voit, un echec n'arrete pas les autres, et aucune requete ne depasse
 * le delai du proxy. Pendant l'envoi, la modale ne se ferme pas : la boucle
 * vit dans ce composant.
 *
 * DEUX TEMPS (2026-11-04) : la modale s'affiche aussitot avec ce que la base
 * sait — les salaries, leurs adresses, ce qui est deja parti —, et la colonne
 * Fiche se remplit quand le Drive a repondu, environ 3 s plus tard. Rien ne se
 * coche avant : une fiche ne part que si son fichier a ete trouve. Apres un
 * envoi, seule la base se relit : le Drive n'a pas change.
 *
 * LA COCHE SE DEDUIT DES DONNEES tant qu'on n'y a pas touche, elle n'est
 * jamais initialisee depuis le chargement : voir le piege de `useRessource`.
 * Prete et jamais envoyee, une fiche est cochee. Une fiche deja envoyee ne
 * l'est pas, sauf quand on a ouvert la modale pour ce seul salarie.
 */
export function EnvoiFichesPaie({
  anneeId,
  mois,
  contratId,
  onFermer,
}: {
  anneeId: string;
  mois: string;
  /** Un seul salarie ; absent, tous ceux du mois. */
  contratId: string | null;
  onFermer: () => void;
}) {
  // Le premier temps : la base seule, immediate.
  const etat = useRessource(
    () => api.get<EtatFichesPaie>('/api/paie/fiches', { anneeId, mois, contratId: contratId ?? undefined }),
    [anneeId, mois, contratId],
  );
  // Le second : le Drive, une fois qu'on sait qu'il peut etre lu. Pour un seul
  // salarie, le serveur ne lit que lui.
  const lisible = etat.donnees !== null && etat.donnees.blocage === null;
  const lecture = useRessource(
    () =>
      lisible
        ? api.get<{ lignes: BulletinFichePaie[] }>('/api/paie/fiches/bulletins', {
            anneeId,
            mois,
            contratId: contratId ?? undefined,
          })
        : Promise.resolve(null),
    [anneeId, mois, contratId, lisible],
  );
  const bulletins = new Map((lecture.donnees?.lignes ?? []).map((b) => [b.contrat_id, b]));
  const driveLu = lecture.donnees !== null;
  const lectureEnCours = lisible && !driveLu && lecture.erreur === null;
  const [choix, setChoix] = useState<Record<string, boolean>>({});
  const [resultats, setResultats] = useState<Record<string, Resultat>>({});
  // L'avancement du lot en cours ; null quand rien ne part.
  const [avancement, setAvancement] = useState<{ fait: number; total: number } | null>(null);
  const enCours = avancement !== null;

  const donnees = etat.donnees;
  const lignes = donnees?.lignes ?? [];
  const essaiSansAdresse = donnees?.essai !== null && donnees?.essai !== undefined && !donnees.essai.adresse;
  const bloque = !donnees || donnees.blocage !== null || essaiSansAdresse;

  const bulletinDe = (l: LigneFichePaie) => bulletins.get(l.contrat_id);
  const envoyable = (l: LigneFichePaie) =>
    !bloque && l.motif_adresse === null && Boolean(bulletinDe(l)?.fichier);
  const cochee = (l: LigneFichePaie) => {
    if (!envoyable(l) || resultats[l.contrat_id]?.etat === 'envoye') return false;
    return choix[l.contrat_id] ?? (contratId !== null || l.envoi === null);
  };

  const cochees = lignes.filter(cochee);
  const toutes = lignes.filter(envoyable).filter((l) => resultats[l.contrat_id]?.etat !== 'envoye');
  const toutCoche = toutes.length > 0 && toutes.every(cochee);

  const envoyer = async () => {
    const aRenvoyer = cochees.filter((l) => l.envoi !== null);
    if (aRenvoyer.length > 0) {
      const noms = aRenvoyer.map((l) => `${l.prenom_usuel} ${(l.nom ?? '').toUpperCase()}`.trim()).join(', ');
      const question =
        aRenvoyer.length > 1
          ? `${noms} ont déjà reçu leur fiche. La leur envoyer de nouveau ?`
          : `${noms} a déjà reçu sa fiche. La lui envoyer de nouveau ?`;
      if (!window.confirm(question)) return;
    }

    // L'une apres l'autre, dans l'ordre affiche.
    const lot = cochees;
    for (const [rang, l] of lot.entries()) {
      setAvancement({ fait: rang, total: lot.length });
      setResultats((courants) => ({ ...courants, [l.contrat_id]: { etat: 'en_cours' } }));
      try {
        const envoi = await api.post<FicheEnvoyee>('/api/paie/fiches/envoi', {
          anneeId,
          mois,
          contratId: l.contrat_id,
          ...(l.envoi !== null ? { renvoi: true } : {}),
        });
        setResultats((courants) => ({ ...courants, [l.contrat_id]: { etat: 'envoye', adresse: envoi.adresse } }));
      } catch (cause) {
        const message = cause instanceof ErreurApi ? cause.message : 'Envoi impossible.';
        setResultats((courants) => ({ ...courants, [l.contrat_id]: { etat: 'echec', message } }));
      }
    }
    setAvancement(null);
    setChoix({});
    etat.recharger();
  };

  const valeurs = Object.values(resultats);
  const nbEnvoyees = valeurs.filter((r) => r.etat === 'envoye').length;
  const nbEchecs = valeurs.filter((r) => r.etat === 'echec').length;

  const etatLigne = (l: LigneFichePaie) => {
    const resultat = resultats[l.contrat_id];
    if (resultat?.etat === 'en_cours') return <span className="text-xs text-encre-500">envoi…</span>;
    if (resultat?.etat === 'envoye') return <Badge ton="succes">envoyée à {resultat.adresse}</Badge>;
    if (resultat?.etat === 'echec') return <span className="text-xs text-red-700">{resultat.message}</span>;
    const motif = bulletinDe(l)?.motif ?? l.motif_adresse;
    if (motif) return <span className="text-xs text-amber-800">{motif}</span>;
    if (l.envoi?.statut === 'envoye') {
      return (
        <Infobulle texte={`Envoyée à ${l.envoi.adresse}`}>
          <Badge ton="neutre">envoyée le {formaterDateHeure(l.envoi.date)}</Badge>
        </Infobulle>
      );
    }
    if (l.envoi?.statut === 'en_cours') {
      return (
        <Infobulle
          texte={`Une tentative vers ${l.envoi.adresse} n’a pas été confirmée : vérifiez les messages envoyés du compte technique avant de renvoyer.`}
        >
          <Badge ton="alerte">non confirmée le {formaterDateHeure(l.envoi.date)}</Badge>
        </Infobulle>
      );
    }
    if (!bulletinDe(l)?.fichier) return <span className="text-xs text-encre-300">—</span>;
    return <span className="text-xs text-encre-500">prête</span>;
  };

  return (
    <Modale
      titre={
        contratId && lignes[0]
          ? `Envoyer sa fiche de paie à ${lignes[0].prenom_usuel} ${(lignes[0].nom ?? '').toUpperCase()}`.trim()
          : 'Envoyer les fiches de paie'
      }
      ouverte
      large
      onFermer={() => {
        if (!enCours) onFermer();
      }}
    >
      {etat.chargement && !donnees && <Chargement />}
      {etat.erreur && <Erreur message={etat.erreur} onReessayer={etat.recharger} />}

      {donnees && (
        <div className="space-y-3">
          <p className="text-sm text-encre-600">
            Fiches de paie de <span className="font-medium text-encre-900">{donnees.libelle_mois}</span>
            {donnees.expediteur && (
              <>
                , envoyées depuis <span className="font-medium text-encre-900">{donnees.expediteur}</span>
              </>
            )}
            {donnees.repondre_a && (
              <>
                {' '}
                ; les réponses arrivent sur <span className="font-medium text-encre-900">{donnees.repondre_a}</span>
              </>
            )}
            .
          </p>

          {donnees.essai && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {donnees.essai.adresse
                ? `Poste de développement : rien ne part vers les salariés. Tout est envoyé à ${donnees.essai.adresse}, et le sujet nomme le vrai destinataire.`
                : 'Poste de développement : rien ne peut partir, la base porte de vraies adresses. Renseignez ENVOI_EMAIL_TEST dans backend/.env pour essayer.'}
            </p>
          )}

          {lecture.erreur && (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              Le Drive n’a pas pu être lu : {lecture.erreur}
            </p>
          )}

          {donnees.blocage && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{donnees.blocage}</p>
          )}

          {lignes.length === 0 ? (
            <p className="text-sm text-encre-500">
              {contratId ? 'Ce salarié n’a pas de fiche ce mois-ci.' : 'Aucun salarié déclaré ce mois-ci.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-encre-200 text-left text-xs uppercase tracking-wide text-encre-500">
                  <th className="w-6 py-1.5 pr-2 font-medium">
                    {!contratId && (
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        data-navigation
                        aria-label="Tout cocher"
                        checked={toutCoche}
                        disabled={enCours || toutes.length === 0}
                        onChange={() =>
                          setChoix(Object.fromEntries(toutes.map((l) => [l.contrat_id, !toutCoche])))
                        }
                      />
                    )}
                  </th>
                  <th className="py-1.5 pr-3 font-medium">Salarié</th>
                  <th className="py-1.5 pr-3 font-medium">Adresse</th>
                  <th className="py-1.5 pr-3 font-medium">Fiche</th>
                  <th className="py-1.5 font-medium">État</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((l) => (
                  <tr key={l.contrat_id} className="border-b border-encre-100 align-top">
                    <td className="py-1.5 pr-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        data-navigation
                        aria-label={`Envoyer sa fiche à ${l.prenom_usuel} ${l.nom ?? ''}`.trim()}
                        checked={cochee(l)}
                        disabled={enCours || !envoyable(l) || resultats[l.contrat_id]?.etat === 'envoye'}
                        onChange={(e) => setChoix((courant) => ({ ...courant, [l.contrat_id]: e.target.checked }))}
                      />
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3 font-medium">
                      {l.prenom_usuel} <span className="uppercase text-encre-500">{l.nom ?? ''}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-xs text-encre-600">{l.email ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-xs">
                      {(() => {
                        const fichier = bulletinDe(l)?.fichier;
                        if (!fichier) {
                          return lectureEnCours ? (
                            <span className="text-encre-400">lecture du Drive…</span>
                          ) : (
                            <span className="text-encre-300">—</span>
                          );
                        }
                        return (
                          <>
                            {fichier.url ? (
                              <a href={fichier.url} target="_blank" rel="noreferrer" className="underline">
                                {fichier.nom}
                              </a>
                            ) : (
                              fichier.nom
                            )}
                            {fichier.autres_versions > 0 && (
                              <Infobulle
                                texte={`${fichier.autres_versions + 1} fichiers pour ce mois : le plus récent part.`}
                                className="ml-1.5 text-amber-700"
                              >
                                ({fichier.autres_versions + 1} versions)
                              </Infobulle>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td className="py-1.5">{etatLigne(l)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {nbEnvoyees + nbEchecs > 0 && !enCours && (
            <p className={`text-sm ${nbEchecs > 0 ? 'text-red-700' : 'text-emerald-800'}`}>
              {nbEnvoyees} fiche{nbEnvoyees > 1 ? 's' : ''} envoyée{nbEnvoyees > 1 ? 's' : ''}
              {nbEchecs > 0 ? `, ${nbEchecs} en échec : elles restent à envoyer.` : '.'}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <button type="button" className="bouton-secondaire" disabled={enCours} onClick={onFermer}>
              Fermer
            </button>
            <button
              type="button"
              className="bouton-principal"
              disabled={enCours || cochees.length === 0}
              onClick={() => void envoyer()}
            >
              {avancement
                ? `Envoi en cours… ${avancement.fait}/${avancement.total}`
                : lectureEnCours
                  ? 'Lecture du Drive…'
                  : `Envoyer ${cochees.length} fiche${cochees.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}
    </Modale>
  );
}
